import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Gift, Megaphone, ShieldCheck, Wallet } from 'lucide-react';
import { useNotifications } from '../../contexts/NotificationContext';
import { formatNotificationTime } from '../../utils/notificationData';
import { getThemeColor } from '../../config/appConfig';
import './notifications.css';

const THEME = getThemeColor('primary');

const typeIcon = (type = '') => {
  const key = String(type).toLowerCase();
  if (key.includes('promo') || key.includes('bonus')) return Gift;
  if (key.includes('withdraw') || key.includes('deposit') || key.includes('payout')) return Wallet;
  if (key.includes('kyc') || key.includes('verif')) return ShieldCheck;
  if (key.includes('reward') || key.includes('loyalty')) return Megaphone;
  return Bell;
};

const statusDotClass = (status) => {
  if (status === 'Connected') return 'notif-bell__status is-connected';
  if (status === 'Connecting') return 'notif-bell__status is-connecting';
  return 'notif-bell__status is-disconnected';
};

const NotificationBell = () => {
  const {
    notifications,
    unreadCount,
    connectionStatus,
    markAllSeen,
    markOneSeen,
  } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleToggle = () => {
    setOpen((v) => {
      const next = !v;
      if (next) markAllSeen();
      return next;
    });
  };

  const recent = notifications.slice(0, 8);

  return (
    <div
      className="notif-bell"
      ref={ref}
      style={{
        '--notif-primary': THEME,
      }}
    >
      <button
        type="button"
        className={`notif-bell__trigger${open ? ' is-open' : ''}`}
        onClick={handleToggle}
        aria-expanded={open}
        aria-label={unreadCount > 0 ? `${unreadCount} unread notifications` : 'Notifications'}
      >
        <span className="notif-bell__icon-wrap">
          <Bell className="notif-bell__icon" strokeWidth={2} aria-hidden="true" />
          {unreadCount > 0 ? (
            <span className="notif-bell__badge">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          ) : null}
        </span>
      </button>

      {open ? (
        <div className="notif-bell__dropdown" role="menu">
          <div className="notif-bell__head">
            <div>
              <strong>Notifications</strong>
              <span className={statusDotClass(connectionStatus)} title={connectionStatus}>
                {connectionStatus}
              </span>
            </div>
            <Link to="/notifications" className="notif-bell__all" onClick={() => setOpen(false)}>
              View all
            </Link>
          </div>

          <div className="notif-bell__list">
            {recent.length === 0 ? (
              <p className="notif-bell__empty">No notifications yet.</p>
            ) : (
              recent.map((item) => {
                const Icon = typeIcon(item.type);
                return (
                  <button
                    key={item.identity}
                    type="button"
                    className={`notif-bell__item${!item.isRead ? ' is-unread' : ''}`}
                    onClick={() => markOneSeen(item.identity)}
                  >
                    <span className="notif-bell__icon" style={{ color: THEME }}>
                      <Icon className="w-4 h-4" />
                    </span>
                    <span className="notif-bell__body">
                      <strong>{item.title}</strong>
                      {item.message ? <em>{item.message}</em> : null}
                      <small>{formatNotificationTime(item.createdDateTime)}</small>
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default NotificationBell;
